import { useEffect, useState, useMemo, useRef } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  Button,
  Alert,
  Form,
  Row,
  Col,
  Card,
  Image,
  ListGroup,
  Modal,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { PayPalButtons, usePayPalScriptReducer } from '@paypal/react-paypal-js';

import Message from '../Components/Message';
import Loader from '../Components/Loader';

import {
  useGetOrderDetailsQuery,
  usePayOrderMutation,
  usePartialPayOrderMutation,
  useGetPayPalClientIdQuery,
  useDeliverOrderMutation,
  useSetPickupWindowMutation,
  useMarkOrderCollectedMutation,
  useCreateShippingLabelMutation,
  useMarkOrdersSeenMutation,
  useClearOrderPaymentMutation,
  useAssignOrderCustomerMutation,
} from '../slices/ordersApiSlice';
import { useGetUsersQuery } from '../slices/usersApiSlice';
import { useGenerateInvoiceMutation } from '../slices/invoiceApiSlice';
import BackButton from "../Components/BackButton";
import { toBackendUrl } from '../utils/apiBase';
import { fmt as fmtPrice } from '../utils/cartUtils';
import { DEFAULT_PICKUP_LOCATION } from '../utils/shippingPolicy';
import { buildProductUrl, getCurrentRoute } from '../utils/navigation';
import ProductLocationsSummary from '../Components/admin/ProductLocationsSummary';
import { getTotalItemQuantity } from '../utils/itemCount';

// ---------- helpers ----------
const fmt = fmtPrice;

// Small badge for return status (same palette as admin screen)
const ReturnStatusBadge = ({ s }) => {
  const map = {
    requested: 'secondary',
    authorized: 'primary',
    in_transit: 'warning',
    received: 'warning',
    refunded: 'success',
    partial_refunded: 'info',
    rejected: 'danger',
    closed: 'dark',
  };
  return <span className={`badge bg-${map[s] || 'secondary'}`}>{s || 'requested'}</span>;
};



function isEomOrder(order) {
  const paymentMethod = String(order?.paymentMethod || '').toLowerCase();
  return (
    paymentMethod.includes('end of month') ||
    paymentMethod.includes('eom') ||
    paymentMethod.includes('monthly') ||
    order?.user?.endOfMonthPaymentEnabled === true
  );
}

function isCancelledOrder(order) {
  return order?.isCancelled || String(order?.status || '').toLowerCase() === 'cancelled';
}

const OrderScreen = () => {
  const location = useLocation();
  const { id: orderId } = useParams();

  // --- Load order data ---
  const {
    data: order,
    refetch,
    isLoading,
    error,
  } = useGetOrderDetailsQuery(orderId);

  const { userInfo } = useSelector((s) => s.auth || {});

  // ---- Admin pickup form state ----
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [instructions, setInstructions] = useState('');
  const [collectedBy, setCollectedBy] = useState('');
  const [markPaid, setMarkPaid] = useState(true);
  const [partialPercent, setPartialPercent] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerUserId, setCustomerUserId] = useState('');
  const [showCustomerChangeConfirm, setShowCustomerChangeConfirm] =
    useState(false);

  // ---- Mutations ----
  const [createLabel, { isLoading: creatingLabel }] =
    useCreateShippingLabelMutation();

  const [setPickupWindow, { isLoading: savingWindow }] =
    useSetPickupWindowMutation();

  const [markCollected, { isLoading: savingCollect }] =
    useMarkOrderCollectedMutation();

  const [payOrder, { isLoading: loadingPay }] = usePayOrderMutation();
  const [partialPayOrder, { isLoading: loadingPartialPay }] =
    usePartialPayOrderMutation();
  const [clearOrderPayment, { isLoading: loadingClearPayment }] =
    useClearOrderPaymentMutation();
  const [deliverOrder, { isLoading: loadingDeliver }] =
    useDeliverOrderMutation();

  const [generateInvoice, { isLoading: isInvoiceLoading }] =
    useGenerateInvoiceMutation();
  const [markOrdersSeen] = useMarkOrdersSeenMutation();
  const [assignOrderCustomer, { isLoading: isAssigningCustomer }] =
    useAssignOrderCustomerMutation();
  const { data: users = [], isLoading: isLoadingUsers } = useGetUsersQuery(
    undefined,
    { skip: !userInfo?.isAdmin }
  );

  const lastMarkedOrderId = useRef(null);

  // ---- PayPal setup ----
  const [{ isPending }, paypalDispatch] = usePayPalScriptReducer();
  const { data: paypal, isLoading: loadingPayPal, error: errorPayPal } =
    useGetPayPalClientIdQuery();

  // Pre-fill pickup window inputs when order loads
  useEffect(() => {
    if (!order) return;
    const toInput = (d) =>
      d ? new Date(d).toISOString().slice(0, 16) : '';

    setStart(toInput(order.pickupWindowStart));
    setEnd(toInput(order.pickupWindowEnd));
    setInstructions(order.pickupInstructions || '');
    if (isEomOrder(order) && !order.isPaid) {
      setMarkPaid(false);
    }
  }, [order]);

  useEffect(() => {
    setCustomerUserId(String(order?.user?._id || ''));
  }, [order?.user?._id]);

  const customerOptions = useMemo(() => {
    const term = customerSearch.trim().toLowerCase();
    return users.filter((user) => {
      if (!term) return true;
      return `${user.name || ''} ${user.email || ''}`.toLowerCase().includes(term);
    });
  }, [users, customerSearch]);

  const selectedCustomer = users.find(
    (user) => String(user._id) === customerUserId
  );

  const saveCustomerAssignment = async () => {
    if (!customerUserId) {
      toast.error('Please select a customer account.');
      return;
    }
    const currentUserId = String(order?.user?._id || '');
    if (currentUserId === customerUserId) {
      toast.info('This order is already linked to this customer.');
      return;
    }
    try {
      await assignOrderCustomer({ orderId, userId: customerUserId }).unwrap();
      setShowCustomerChangeConfirm(false);
      toast.success('Customer account linked to order.');
      await refetch();
    } catch (err) {
      toast.error(err?.data?.message || err?.message || 'Failed to link customer.');
    }
  };

  const handleAssignCustomer = () => {
    if (!customerUserId) {
      toast.error('Please select a customer account.');
      return;
    }
    if (String(order?.user?._id || '') === customerUserId) {
      toast.info('This order is already linked to this customer.');
      return;
    }
    if (order?.user) {
      setShowCustomerChangeConfirm(true);
      return;
    }
    saveCustomerAssignment();
  };

  // Load PayPal script if needed
  useEffect(() => {
    if (!errorPayPal && !loadingPayPal && paypal?.clientId) {
      const loadPayPalScript = async () => {
        paypalDispatch({
          type: 'resetOption',
          value: {
            'client-id': paypal.clientId,
            currency: 'CAD',
            intent: 'capture',
          },
        });
        paypalDispatch({ type: 'setLoadingStatus', value: 'pending' });
      };

      if (order && !order.isPaid && !window.paypal) {
        loadPayPalScript();
      }
    }
  }, [order, paypal, paypalDispatch, loadingPayPal, errorPayPal]);

  // Mark orders as seen only when an admin views the details page
  useEffect(() => {
    if (!userInfo?.isAdmin) return;
    if (!order?._id) return;

    if (order.adminSeen) {
      lastMarkedOrderId.current = order._id;
      return;
    }

    if (lastMarkedOrderId.current === order._id) return;

    (async () => {
      try {
        await markOrdersSeen().unwrap();
        lastMarkedOrderId.current = order._id;
      } catch (err) {
        console.error('Failed to mark orders as seen', err);
      }
    })();
  }, [order?._id, order?.adminSeen, userInfo?.isAdmin, markOrdersSeen]);

  // -------- derived shipping info for admin card --------
  const shippingMeta = order?.shipping || {};
  const selectedRate = shippingMeta.rate || {};

  const carrierServiceLabel = useMemo(() => {
    if (!selectedRate?.carrier && !selectedRate?.service) return '—';
    return `${selectedRate.carrier || ''} — ${
      selectedRate.service || ''
    }`.trim();
  }, [selectedRate]);

  const rateDisplay = useMemo(() => {
    if (!selectedRate?.rate) return '—';
    const amount = Number(selectedRate.rate);
    const cur = selectedRate.currency || 'CAD';
    return `$${amount.toFixed(2)} ${cur}`;
  }, [selectedRate]);

  // ----- PayPal callbacks -----
  function onApprove(_data, actions) {
    return actions.order.capture().then(async function (details) {
      try {
        await payOrder({ orderId, details });
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || err.message);
        return;
      }
      toast.success('Payment successful');
    });
  }

  function onError(err) {
    toast.error(err.message);
  }

  function createPaypalOrder(_data, actions) {
    if (!order?.totalPrice) {
      return Promise.reject(new Error('Order total is missing'));
    }
    return actions.order
      .create({
        purchase_units: [{ amount: { value: String(order.totalPrice) } }],
      })
      .then((newOrderId) => newOrderId);
  }

  // ----- Admin handlers -----

  // Buy label using the already-selected rate stored on the order
  const handleCreateLabelNow = async () => {
    try {
      await createLabel(orderId).unwrap();
      toast.success('Shipping label created');
      refetch();
    } catch (err) {
      toast.error(
        err?.data?.message ||
          err?.message ||
          'Failed to create shipping label'
      );
    }
  };

  const handlePrintLabel = () => {
    const labelUrl = order?.shipping?.labelUrl;
    if (!labelUrl) {
      toast.error('No label yet');
      return;
    }
    const final = toBackendUrl(labelUrl);
    window.open(final, '_blank', 'noopener,noreferrer');
  };

  const handleCopyTracking = async () => {
    const code = order?.shipping?.trackingCode;
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      toast.success('Tracking copied');
    } catch {
      toast.error('Could not copy tracking');
    }
  };

  const deliverOrderHandler = async () => {
    try {
      await deliverOrder(orderId);
      refetch();
      toast.success('Order delivered');
    } catch (err) {
      toast.error(err?.data?.message || err.message);
    }
  };

  const handlePrintInvoice = async () => {
    try {
      // If invoice already exists, use it. Otherwise generate then open.
      let invNum =
        order?.documentRefs?.invoiceNumber || order?.invoice || null;
      let url = '';

      if (!invNum) {
        const res = await generateInvoice({
          orderId: order._id,
          force: true,
        }).unwrap();

        invNum =
          res?.invoiceNumber ||
          res?.number ||
          res?.invoice?.number ||
          null;

        url =
          res?.publicDownloadUrl ||
          res?.absoluteDownloadUrl ||
          (invNum
            ? `/api/invoices/${encodeURIComponent(
                invNum
              )}/pdf/public`
            : '');
      } else {
        url = `/api/invoices/${encodeURIComponent(
          invNum
        )}/pdf/public`;
      }

      if (!url) throw new Error('Invoice URL missing.');
      const abs = toBackendUrl(url);
      const finalUrl = `${abs}${
        abs.includes('?') ? '&' : '?'
      }print=1&t=${Date.now()}`;

      window.open(finalUrl, '_blank', 'noopener,noreferrer');
    } catch (e) {
      toast.error(
        e?.data?.message ||
          e?.message ||
          'Failed to open invoice for printing'
      );
    }
  };

  const handleDownloadInvoice = async () => {
    try {
      const res = await generateInvoice({
        orderId: order._id,
        force: true,
      }).unwrap();

      const base =
        res?.downloadUrl ||
        res?.absoluteDownloadUrl ||
        res?.pdfPath ||
        '';
      if (!base) {
        toast.error('Invoice URL missing');
        return;
      }

      const abs = toBackendUrl(base);
      const url = `${abs}${
        abs.includes('?') ? '&' : '?'
      }t=${Date.now()}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error(err);
      toast.error(
        err?.data?.message ||
          err.error ||
          'Failed to generate invoice'
      );
    }
  };

  const handleSendPickupWindow = async () => {
    try {
      if (!start || !end) throw new Error('Please choose start and end.');
      await setPickupWindow({
        orderId,
        start,
        end,
        instructions,
      }).unwrap();
      toast.success('Pickup window saved & email sent.');
      refetch();
    } catch (err) {
      toast.error(
        err?.data?.message ||
          err?.message ||
          'Failed to set pickup window.'
      );
    }
  };

  const handleMarkCollected = async (e) => {
    e.preventDefault();
    try {
      await markCollected({
        orderId,
        collectedBy,
        markPaid,
      }).unwrap();
      toast.success('Order marked as collected.');
      refetch();
    } catch (err) {
      toast.error(
        err?.data?.message ||
          err?.message ||
          'Failed to mark collected.'
      );
    }
  };

  // ----- Loading / Error -----
  if (isLoading) return <Loader />;
  if (error) {
    return (
      <Message variant="danger">
        {error?.data?.message || 'Error loading order'}
      </Message>
    );
  }

  const isPickup = order?.fulfillmentMethod === 'pickup';
  const eomOrder = isEomOrder(order);
  const cancelledOrder = isCancelledOrder(order);
  const isFinalized = order?.isDelivered || !!order?.collectedAt;
  const pickupActionsDisabled = isFinalized || !isPickup || cancelledOrder;
  const isShippingLocked =
    isPickup || order?.isDelivered || cancelledOrder;
  const canAdminFulfill =
    userInfo?.isAdmin &&
    !cancelledOrder &&
    !order?.isDelivered &&
    (order?.isPaid || eomOrder);
  const pickupLocation = order?.pickupLocation || DEFAULT_PICKUP_LOCATION;

  // ----- Safe accessors -----
  const customerName =
    order?.user?.name || order?.guest?.name || 'Guest';
  const customerEmail =
    order?.user?.email || order?.guest?.email || '';
  const customerPhone = order?.guest?.phone || '';
  const items = order?.orderItems || [];
  const shippingAddress = order?.shippingAddress;
  const normalizedCountry =
    shippingAddress?.country &&
    String(shippingAddress.country)
      .trim()
      .toUpperCase();
  const addressParts = [
    shippingAddress?.address,
    shippingAddress?.city,
    shippingAddress?.province ||
      shippingAddress?.state,
    shippingAddress?.postalCode,
    normalizedCountry &&
    (normalizedCountry !== 'CA' ||
      shippingAddress?.address ||
      shippingAddress?.city ||
      shippingAddress?.province ||
      shippingAddress?.state ||
      shippingAddress?.postalCode)
      ? normalizedCountry
      : null,
  ].filter(Boolean);
  const hasShippingAddress =
    addressParts.length > 0;

  const returns = Array.isArray(order?.returns) ? order.returns : [];
  const hasOpenReturn = returns.some((r) =>
    ['requested', 'authorized', 'in_transit', 'received', 'partial_refunded'].includes(r.status)
  );

  // fallback calculation for totals
  const itemsSum = items.reduce(
    (acc, i) =>
      acc + Number(i.price ?? 0) * Number(i.qty ?? 0),
    0
  );

  const itemsPrice = Number(order?.itemsPrice ?? itemsSum);
  const originalItemsPrice = Number(
    items.reduce(
      (acc, i) =>
        acc +
        Number(
          i.originalPrice ??
            i.price ??
            0
        ) *
          Number(i.qty ?? 0),
      0
    )
  );
  const discount = Math.max(
    0,
    originalItemsPrice - itemsPrice
  );
  const shippingPrice = Number(order?.shippingPrice ?? 0);
  const taxPrice = Number(order?.taxPrice ?? 0);
  const totalPrice = Number(
    order?.totalPrice ??
      itemsPrice + shippingPrice + taxPrice
  );
  const paidAmount = Number(order?.paidAmount ?? 0);
  const remainingAmount = Math.max(0, totalPrice - paidAmount);
  const hasPayment = paidAmount > 0;
  const undoPaymentLabel = order?.isPaid
    ? 'Undo Full Payment'
    : 'Undo Partial Payment';

  const displayRef =
    order?.reference ||
    order?.orderRef ||
    (order?._id ? String(order._id).slice(-6).toUpperCase() : '');
  const displayInvoice =
    order?.invoice ||
    order?.invoiceNumber ||
    order?.documentRefs?.invoiceNumber ||
    null;
  const displayHeading =
    displayRef ||
    displayInvoice ||
    order?.orderNumber ||
    order?._id;

  return (
    <>
    <BackButton />
      <h1 className="mb-1">Order {displayHeading}</h1>
      <div className="text-muted mb-3">
        {displayRef && <span className="me-3">Ref: {displayRef}</span>}
        {displayInvoice && <span className="me-3">Invoice: {displayInvoice}</span>}
        {order?._id && <span className="me-3">ID: {order._id}</span>}
      </div>

      {userInfo?.isAdmin && (
        <>
          <Card className="mb-3">
            <Card.Body>
              <Card.Title>Linked Customer Account</Card.Title>
              {order?.user && (
                <p className="mb-3">
                  <strong>{order.user.name}</strong><br />
                  <span className="text-muted">{order.user.email}</span>
                </p>
              )}
              <Row className="g-2 align-items-end">
                <Col md={5}>
                  <Form.Label>Search registered customers</Form.Label>
                  <Form.Control
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    placeholder="Name or email"
                  />
                </Col>
                <Col md={5}>
                  <Form.Label>Customer account</Form.Label>
                  <Form.Select
                    value={customerUserId}
                    onChange={(e) => setCustomerUserId(e.target.value)}
                    disabled={isLoadingUsers}
                  >
                    <option value="">Select a customer</option>
                    {customerOptions.map((user) => (
                      <option key={user._id} value={user._id}>
                        {user.name} — {user.email}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={2} className="d-grid">
                  <Button
                    type="button"
                    onClick={handleAssignCustomer}
                    disabled={
                      !customerUserId ||
                      isAssigningCustomer ||
                      String(order?.user?._id || '') === customerUserId
                    }
                  >
                    {isAssigningCustomer
                      ? 'Saving…'
                      : order?.user
                        ? 'Change Customer'
                        : 'Link Customer'}
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Modal
            show={showCustomerChangeConfirm}
            onHide={() => setShowCustomerChangeConfirm(false)}
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>Change linked customer?</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p className="mb-1">This order is currently linked to:</p>
              <p>
                <strong>{order?.user?.name}</strong><br />
                <span className="text-muted">{order?.user?.email}</span>
              </p>
              <p className="mb-1">It will be reassigned to:</p>
              <p>
                <strong>{selectedCustomer?.name}</strong><br />
                <span className="text-muted">{selectedCustomer?.email}</span>
              </p>
              <p className="mb-0">
                The order will no longer appear in {order?.user?.name}'s My
                Orders and will appear in {selectedCustomer?.name}'s My Orders.
              </p>
            </Modal.Body>
            <Modal.Footer>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowCustomerChangeConfirm(false)}
                disabled={isAssigningCustomer}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={saveCustomerAssignment}
                disabled={isAssigningCustomer}
              >
                {isAssigningCustomer ? 'Saving…' : 'Confirm Change'}
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      )}

      <Row>
        {/* LEFT COLUMN */}
        <Col md={8}>
          <ListGroup variant="flush">
            <ListGroup.Item>
              <h2>{isPickup ? 'Pickup' : 'Shipping'}</h2>

              <p>
                <strong>Name: </strong> {customerName}
              </p>

              {customerEmail && (
                <p>
                  <strong>Email: </strong>{' '}
                  <a href={`mailto:${customerEmail}`}>
                    {customerEmail}
                  </a>
                </p>
              )}

              {customerPhone && (
                <p>
                  <strong>Phone: </strong> {customerPhone}
                </p>
              )}

              {isPickup ? (
                <p className="mb-1">
                  <strong>Pickup: </strong>
                  {pickupLocation}
                </p>
              ) : hasShippingAddress ? (
                <p>
                  <strong>Address: </strong>
                  {addressParts.join(', ')}
                </p>
              ) : null}

              {/* Pickup window info visible to everyone if set */}
              {order?.isReadyForPickup &&
                order?.pickupWindowStart && (
                  <Alert variant="info" className="mt-2">
                    Pickup window:&nbsp;
                    <strong>
                      {new Date(
                        order.pickupWindowStart
                      ).toLocaleString()}
                    </strong>
                    &nbsp;–&nbsp;
                    <strong>
                      {new Date(
                        order.pickupWindowEnd
                      ).toLocaleString()}
                    </strong>
                    {order.pickupInstructions
                      ? ` — ${order.pickupInstructions}`
                      : null}
                  </Alert>
                )}

              {order.isDelivered ? (
                <Message variant="success">
                  Delivered on{' '}
                  {order?.deliveredAt
                    ? new Date(
                        order.deliveredAt
                      ).toLocaleString()
                    : '—'}
                </Message>
              ) : (
                <Message variant="danger">
                  Not Delivered
                </Message>
              )}
            </ListGroup.Item>

            <ListGroup.Item>
              <h2>Payment Method</h2>
              <p>
                <strong>Method: </strong>
                {order.paymentMethod}
              </p>
              {eomOrder && !order.isPaid && (
                <Alert variant="info" className="py-2">
                  This is an End of Month Payment order. Delivery/collection can be completed before payment. Payment will be handled through monthly billing.
                </Alert>
              )}
              {order.isPaid ? (
                <Message variant="success">
                  Paid on{' '}
                  {order?.paidAt
                    ? new Date(
                        order.paidAt
                      ).toLocaleString()
                    : '—'}
                </Message>
              ) : (
                <Message variant="danger">
                  Not paid
                </Message>
              )}
            </ListGroup.Item>

            <ListGroup.Item>
              <h2>Order Items</h2>
              {items.length === 0 ? (
                <Message>
                  Order has no items
                </Message>
              ) : (
                <ListGroup variant="flush">
                  {items.map((item, index) => (
                    <ListGroup.Item
                      key={
                        item.product ||
                        item._id ||
                        index
                      }
                    >
                      <Row className="align-items-center">
                        <Col md={1}>
                          <Image
                            src={item.image}
                            alt={item.displayName || item.title || item.name}
                            fluid
                            rounded
                          />
                        </Col>
                        <Col>
                          <Link
                            to={buildProductUrl(item.product || item._id, location)}
                            state={{ from: getCurrentRoute(location) }}
                          >
                            {item.displayName || item.title || item.name}
                          </Link>
                          {(item.tierPriceApplied ||
                            Number(item.originalPrice ?? 0) >
                              Number(item.price ?? 0)) && (
                            <div className="mt-1 d-flex flex-wrap gap-1">
                              {item.tierPriceApplied && (
                                <span className="badge bg-info">
                                  Wholesale Price
                                </span>
                              )}
                              {Number(item.originalPrice ?? 0) >
                                Number(item.price ?? 0) &&
                                !item.tierPriceApplied && (
                                  <span className="badge bg-success">
                                    Discounted Price
                                  </span>
                                )}
                            </div>
                          )}
                          {item.sku && (
                            <div className="text-muted small">
                              SKU: {item.sku}
                            </div>
                          )}
                        </Col>
                        {userInfo?.isAdmin && (
                          <Col md={3} className="small">
                            <strong>Pick from:</strong>
                            <ProductLocationsSummary product={item} />
                          </Col>
                        )}
                        <Col md={4}>
                          <div>
                            {item.qty} × ${fmt(item.price)} = $
                            {fmt(
                              Number(
                                item.qty ?? 0
                              ) *
                                Number(
                                  item.price ?? 0
                                )
                            )}
                          </div>
                          {Number(item.originalPrice ?? 0) >
                            Number(item.price ?? 0) && (
                            <div className="small text-muted">
                              <span className="text-decoration-line-through">
                                ${fmt(item.originalPrice)}
                              </span>
                              <span className="ms-2">
                                Save $
                                {fmt(
                                  (Number(
                                    item.originalPrice ?? 0
                                  ) -
                                    Number(
                                      item.price ?? 0
                                    )) *
                                    Number(item.qty ?? 0)
                                )}
                              </span>
                              <div className="mt-1">
                                Reason:{' '}
                                {item.tierPriceApplied
                                  ? 'Wholesale pricing applied.'
                                  : 'Discount applied.'}
                              </div>
                            </div>
                          )}
                        </Col>
                      </Row>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </ListGroup.Item>
          </ListGroup>
        </Col>

        {/* RIGHT COLUMN */}
        <Col md={4}>
          <Card>
            <ListGroup variant="flush">
              {/* Summary Header */}
              <ListGroup.Item>
                <h2>Order Summary</h2>
              </ListGroup.Item>

              {/* Totals */}
              <ListGroup.Item>
                <Row>
                  <Col>Items</Col>
                  <Col>${fmt(itemsPrice)}</Col>
                </Row>
                {discount > 0 && (
                  <>
                    <Row className="text-muted">
                      <Col>
                        Items before discount
                      </Col>
                      <Col>
                        ${fmt(originalItemsPrice)}
                      </Col>
                    </Row>
                    <Row>
                      <Col>Discount</Col>
                      <Col>
                        - ${fmt(discount)}
                      </Col>
                    </Row>
                  </>
                )}
                <Row>
                  <Col>Shipping</Col>
                  <Col>
                    ${fmt(shippingPrice)}
                    {order?.freeDeliveryApplied && Number(order?.shippingPrice ?? 0) === 0 && (
                      <div className="text-muted small">Free delivery applied.</div>
                    )}
                  </Col>
                </Row>
                <Row>
                  <Col>Tax</Col>
                  <Col>${fmt(taxPrice)}</Col>
                </Row>
                <Row>
                  <Col>Total Items</Col>
                  <Col>{getTotalItemQuantity(order?.orderItems)}</Col>
                </Row>
                <Row>
                  <Col>Total</Col>
                  <Col>${fmt(totalPrice)}</Col>
                </Row>
                {paidAmount > 0 && (
                  <>
                    <Row>
                      <Col>Paid</Col>
                      <Col>${fmt(paidAmount)}</Col>
                    </Row>
                    <Row>
                      <Col>Remaining</Col>
                      <Col>${fmt(remainingAmount)}</Col>
                    </Row>
                  </>
                )}
              </ListGroup.Item>

              {/* Download Invoice */}
              <ListGroup.Item>
                <div className="d-grid">
                  <Button
                    type="button"
                    variant="outline-primary"
                    className="fw-semibold py-2"
                    onClick={handleDownloadInvoice}
                    disabled={!order || isInvoiceLoading}
                  >
                    {isInvoiceLoading
                      ? 'Generating…'
                      : 'Download Invoice (PDF)'}
                  </Button>
                </div>
              </ListGroup.Item>

              {/* PayPal region if not paid */}
              {!order.isPaid && (
                <ListGroup.Item>
                  {loadingPay && <Loader />}
                  {!userInfo?.isAdmin && (
                    loadingPayPal ? (
                      <Loader />
                    ) : !paypal?.enabled ? (
                      <p className="text-muted small mb-2">
                        Online payment is not currently available. Please contact us to arrange payment.
                      </p>
                    ) : isPending ? (
                      <Loader />
                    ) : (
                      <PayPalButtons
                        createOrder={createPaypalOrder}
                        onApprove={onApprove}
                        onError={onError}
                      />
                    )
                  )}
                  {userInfo?.isAdmin && (
                    <>
                      <div className="d-grid mt-2">
                        <Button
                          type="button"
                          variant="secondary"
                          className="fw-semibold"
                          onClick={async () => {
                            try {
                              await payOrder({
                                orderId,
                                details: {
                                  id: `manual-${Date.now()}`,
                                  status: 'COMPLETED',
                                  update_time: new Date().toISOString(),
                                  email_address:
                                    order?.guest?.email ||
                                    order?.user?.email ||
                                    '',
                                },
                              }).unwrap();
                              toast.success('Payment marked as received.');
                              refetch();
                            } catch (err) {
                              toast.error(
                                err?.data?.message ||
                                  err?.message ||
                                  'Failed to mark paid.'
                              );
                            }
                          }}
                        >
                          Confirm Payment (Admin)
                        </Button>
                      </div>
                      <div className="mt-3">
                        <Form.Label>
                          Partial payment (% of total)
                        </Form.Label>
                        <div className="d-flex gap-2">
                          <Form.Control
                            type="number"
                            min="1"
                            max="100"
                            step="1"
                            value={partialPercent}
                            onChange={(e) =>
                              setPartialPercent(e.target.value)
                            }
                            placeholder="e.g. 25"
                          />
                          <Button
                            variant="outline-secondary"
                            disabled={loadingPartialPay}
                            onClick={async () => {
                              try {
                                const percent = Number(partialPercent);
                                await partialPayOrder({
                                  orderId,
                                  percent,
                                }).unwrap();
                                toast.success('Partial payment recorded.');
                                setPartialPercent('');
                                refetch();
                              } catch (err) {
                                toast.error(
                                  err?.data?.message ||
                                    err?.message ||
                                    'Failed to record partial payment.'
                                );
                              }
                            }}
                          >
                            {loadingPartialPay ? 'Saving…' : 'Apply'}
                          </Button>
                        </div>
                      </div>
                      {hasPayment && (
                        <div className="mt-3">
                          <Button
                            variant="outline-danger"
                            className="w-100 fw-semibold"
                            disabled={loadingClearPayment}
                            onClick={async () => {
                              const action =
                                order?.isPaid
                                  ? 'full'
                                  : 'partial';
                              const confirmed = window.confirm(
                                `Undo ${action} payment for this order?`
                              );
                              if (!confirmed) return;
                              try {
                                await clearOrderPayment(orderId).unwrap();
                                toast.success('Payment cleared.');
                                refetch();
                              } catch (err) {
                                toast.error(
                                  err?.data?.message ||
                                    err?.message ||
                                    'Failed to undo payment.'
                                );
                              }
                            }}
                          >
                            {loadingClearPayment
                              ? 'Reverting…'
                              : undoPaymentLabel}
                          </Button>
                          <div className="form-text text-muted mt-1">
                            Clears recorded payment amounts and marks the order unpaid.
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </ListGroup.Item>
              )}

              {loadingDeliver && <Loader />}

              {/* Admin-only Deliver button */}
              {canAdminFulfill && !isPickup && (
                  <ListGroup.Item>
                    <Button
                      type="button"
                      className="w-100 fw-semibold py-2 text-white"
                      variant="success"
                      onClick={deliverOrderHandler}
                    >
                      Mark As Delivered
                    </Button>
                  </ListGroup.Item>
                )}
              {!userInfo?.isAdmin && order.isPaid && !hasOpenReturn && (
                <ListGroup.Item>
                  <div className="d-grid">
                    <Button
                      as={Link}
                      to={`/return-request/${order._id}`}
                      variant="danger"
                      className="fw-semibold py-2 text-white"
                    >
                      Request a Return / Refund
                    </Button>
                  </div>
                </ListGroup.Item>
              )}
                
              {/* Returns on this order (customer- & admin-visible) */}
              {returns.length > 0 && (
                <ListGroup.Item>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0">Return Requests</h6>
                    {hasOpenReturn && (
                      <span className="badge bg-warning text-dark">Open</span>
                    )}
                  </div>
                  <div className="d-flex flex-column gap-2">
                    {returns
                      .slice()
                      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                      .map((r) => (
                        <div
                          key={r._id}
                          className="border rounded p-2 d-flex flex-wrap align-items-center gap-2"
                        >
                          <ReturnStatusBadge s={r.status} />
                          <span className="text-muted small">
                            {new Date(r.createdAt).toLocaleString()}
                          </span>
                          <span className="ms-auto small">
                            <strong>RMA:</strong> {r.rmaNumber || r._id.slice(-6).toUpperCase()}
                          </span>
                          {r.trackingCode && (
                            <span className="small">
                              <strong>Tracking:</strong> {r.trackingCode}
                            </span>
                          )}
                          {r.shippingLabelUrl ? (
                            <Button
                              size="sm"
                              variant="outline-primary"
                              className="ms-auto"
                              onClick={() =>
                                window.open(r.shippingLabelUrl, '_blank', 'noopener,noreferrer')
                              }
                            >
                              Open Return Label
                            </Button>
                          ) : null}
                          {userInfo?.isAdmin && (
                            <Button
                              size="sm"
                              variant="outline-dark"
                              onClick={() =>
                                window.location.assign(`/admin/returns?orderId=${order._id}`)
                              }
                            >
                              Manage
                            </Button>
                          )}
                        </div>
                      ))}
                  </div>
                </ListGroup.Item>
              )}
                  

            </ListGroup>
          </Card>

          {/* Admin — Shipping & Label */}
          {userInfo?.isAdmin && (
            <Card className="mt-4">
              <Card.Header className="py-2">
                <strong>
                  Admin — Shipping &amp; Label
                </strong>
              </Card.Header>
              <Card.Body>
                {isShippingLocked && (
                  <Alert variant="secondary" className="mb-3">
                    {isPickup
                      ? 'Shipping actions are locked for pickup orders.'
                      : 'Shipping actions are locked because this order is already delivered.'}
                  </Alert>
                )}
                {/* Carrier / Service */}
                <div className="mb-3">
                  <div className="small text-muted">
                    Carrier / Service
                  </div>
                  <div>{carrierServiceLabel}</div>
                </div>

                {/* Rate */}
                <div className="mb-3">
                  <div className="small text-muted">
                    Rate
                  </div>
                    <div>{rateDisplay}</div>
                </div>

                {/* Tracking */}
                <div className="mb-3">
                  <div className="small text-muted">
                    Tracking #
                  </div>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span>
                      {order?.shipping?.trackingCode ||
                        '—'}
                    </span>
                    {!!order?.shipping
                      ?.trackingCode && (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="fw-semibold py-1 px-2"
                        onClick={handleCopyTracking}
                      >
                        Copy
                      </Button>
                    )}
                  </div>
                </div>

                {/* Label actions */}
                <div className="d-grid gap-2">
                  <Button
                    style={{ backgroundColor: "#9f80d7ff", borderColor: "#6f42c1", color: "white" }}
                    className="w-100 fw-semibold py-2"
                    onClick={handleCreateLabelNow}
                    disabled={creatingLabel || isShippingLocked}
                  >
                    {creatingLabel
                      ? 'Creating Label…'
                      : order?.shipping?.labelUrl
                      ? 'Recreate Label'
                      : 'Create Shipping Label'}
                  </Button>

                  <Button
                    variant="outline-dark"
                    className="w-100 fw-semibold py-2"
                    onClick={handlePrintLabel}
                    disabled={
                      !order?.shipping?.labelUrl || isShippingLocked
                    }
                    title={
                      !order?.shipping?.labelUrl
                        ? 'Create a label first'
                        : 'Open label PDF'
                    }
                  >
                    Print Label
                  </Button>
                </div>

                {/* Tracking status if present */}
                {!!order?.shipping?.lastStatus && (
                  <Alert
                    variant="light"
                    className="mt-3 mb-0"
                  >
                    <div className="small text-muted">
                      Latest tracking status
                    </div>
                    <div className="fw-semibold">
                      {order?.shipping?.lastStatus}
                    </div>
                    {order?.shipping
                      ?.lastCheckpoint && (
                      <div className="text-muted small mt-1">
                        {
                          order
                            ?.shipping
                            ?.lastCheckpoint
                        }
                      </div>
                    )}
                  </Alert>
                )}
              </Card.Body>
            </Card>
          )}

          {/* Admin — Pickup & Counter Actions */}
          {userInfo?.isAdmin && (
              <Card className="mt-4 shadow-sm">
                <Card.Header className="py-2">
                  <strong>
                    Admin — Pickup &amp; Counter
                    Actions
                  </strong>
                </Card.Header>
                <Card.Body>
                {isFinalized && (
                  <Alert variant="secondary" className="mb-3">
                    Pickup actions are locked because this order is already
                    {order?.isDelivered ? ' delivered.' : order?.collectedAt ? ' collected.' : ' unavailable.'}
                  </Alert>
                )}
                {!isPickup && (
                  <Alert variant="info" className="mb-3">
                    Pickup window actions are only available for pickup
                    orders.
                  </Alert>
                )}
                <Form onSubmit={(e) => e.preventDefault()}>
                  {/* Row 1: pickup window + staff fields */}
                  <Row className="g-3 align-items-start">
                    <Col md={4}>
                      <Form.Label className="mb-1">
                        Pickup Start
                      </Form.Label>
                      <Form.Control
                        type="datetime-local"
                        value={start}
                        onChange={(e) =>
                          setStart(e.target.value)
                        }
                        disabled={pickupActionsDisabled}
                        required
                      />
                      <div className="form-text">
                        When the window opens
                      </div>
                    </Col>

                    <Col md={4}>
                      <Form.Label className="mb-1">
                        Pickup End
                      </Form.Label>
                      <Form.Control
                        type="datetime-local"
                        value={end}
                        onChange={(e) =>
                          setEnd(e.target.value)
                        }
                        disabled={pickupActionsDisabled}
                        required
                      />
                      <div className="form-text">
                        When the window closes
                      </div>
                    </Col>

                    <Col md={4}>
                      <Form.Label className="mb-1">
                        Collected By
                      </Form.Label>
                      <Form.Control
                        placeholder="e.g., FH"
                        value={collectedBy}
                        onChange={(e) =>
                          setCollectedBy(
                            e.target.value
                          )
                        }
                        disabled={pickupActionsDisabled}
                      />
                      <div className="form-text">
                        Staff initials when marking pickup collected.
                      </div>
                      <Form.Check
                        className="mt-3"
                        type="checkbox"
                        label="Mark order PAID at pickup"
                        checked={markPaid}
                        onChange={(e) =>
                          setMarkPaid(
                            e.target.checked
                          )
                        }
                        disabled={pickupActionsDisabled}
                      />
                    </Col>
                  </Row>

                  {/* Row 2: instructions */}
                  <Row className="g-3 mt-2">
                    <Col xs={12}>
                      <Form.Label className="mb-1">
                        Instructions (optional)
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={instructions}
                        onChange={(e) =>
                          setInstructions(
                            e.target.value
                          )
                        }
                        placeholder="e.g., Bring photo ID; use side entrance; call on arrival."
                        disabled={pickupActionsDisabled}
                      />
                    </Col>
                  </Row>

                  {/* Row 3: action buttons + status box */}
                  <Row className="g-3 mt-3">
                    <Col md={5}>
                      <div className="d-grid gap-2">
                        {/* Send pickup window email */}
                        <Button
                          type="button"
                          variant="info"
                          className="fw-semibold py-2 text-white"
                          disabled={savingWindow || pickupActionsDisabled}
                          onClick={handleSendPickupWindow}
                        >
                          {savingWindow
                            ? 'Sending…'
                            : 'Send Pickup Window Email'}
                        </Button>

                        {/* Mark collected */}
                        <Button
                          type="button"
                          variant="warning"
                          className="fw-semibold py-2 text-white"
                          disabled={savingCollect || pickupActionsDisabled}
                          onClick={handleMarkCollected}
                        >
                          {savingCollect
                            ? 'Saving…'
                            : 'Mark Pickup Collected'}
                        </Button>

                        {/* Print invoice (only makes sense if paid) */}
                        {order?.isPaid && (
                          <Button
                            type="button"
                            variant="outline-primary"
                            className="fw-semibold py-2"
                            onClick={
                              handlePrintInvoice
                            }
                            title="Open the final invoice PDF to print for the customer"
                            disabled={
                              isInvoiceLoading
                            }
                          >
                            {isInvoiceLoading
                              ? 'Preparing…'
                              : 'Print Final Invoice'}
                          </Button>
                        )}
                      </div>
                    </Col>

                    <Col md={7}>
                      {order?.collectedAt ? (
                        <Alert
                          variant="success"
                          className="mb-0 h-100 d-flex flex-column justify-content-center text-center"
                        >
                          <div className="small text-muted">
                            Collected
                          </div>
                          <strong className="fs-6">
                            {new Date(
                              order.collectedAt
                            ).toLocaleString()}
                          </strong>
                          {order.collectedBy ? (
                            <div className="small mt-1">
                              by{' '}
                              <strong>
                                {
                                  order.collectedBy
                                }
                              </strong>
                            </div>
                          ) : null}
                        </Alert>
                      ) : order?.isReadyForPickup ? (
                        <Alert
                          variant="info"
                          className="mb-0 h-100 d-flex flex-column justify-content-center text-center"
                        >
                          <div className="small text-muted">
                            Status
                          </div>
                          <strong className="fs-6">
                            Ready for Pickup
                          </strong>
                        </Alert>
                      ) : (
                        <Alert
                          variant="secondary"
                          className="mb-0 h-100 d-flex flex-column justify-content-center text-center"
                        >
                          <div className="small text-muted">
                            Status
                          </div>
                          <strong className="fs-6">
                            Window Not Sent
                          </strong>
                        </Alert>
                      )}
                    </Col>
                  </Row>
                </Form>

                {/* Current window banner */}
                {order?.pickupWindowStart &&
                  order?.pickupWindowEnd && (
                    <Alert
                      variant="light"
                      className="mt-3 border"
                    >
                      <div className="d-flex flex-wrap gap-2 align-items-center">
                        <span className="fw-semibold">
                          Pickup window:
                        </span>
                        <span>
                          <strong>
                            {new Date(
                              order.pickupWindowStart
                            ).toLocaleString()}
                          </strong>{' '}
                          —{' '}
                          <strong>
                            {new Date(
                              order.pickupWindowEnd
                            ).toLocaleString()}
                          </strong>
                        </span>
                        {order?.pickupInstructions ? (
                          <span className="text-muted">
                            —{' '}
                            {
                              order
                                ?.pickupInstructions
                            }
                          </span>
                        ) : null}
                      </div>
                    </Alert>
                  )}
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>
    </>
  );
};

export default OrderScreen;
